import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FormGenerator } from '../../helper/FormGenerator';
import { PageList, FormLayout } from '../../components/PageComponents';
import { usePageAPI } from '../../hooks/usePageAPI';
import Head from '../layouts/Head';
import LoadingDashboardContent from '../../components/LoadingDashboardContent';
import { API_URL, FILE_URL, NO_IMG } from '../../constants';
import AdminCustomerEdit from './CustomerEdit ';
import axios from 'axios';
import LazyLoadImage from '../../components/LazyLoadImage';
import { formatDateTime, idFormat } from '../../utils/appUtils';
import { Toggle } from 'rsuite';
import { xorEncrypt } from '../../utils/xorCrypto';
import { useTokens } from '../../context/TokenContext';

const getMediaUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/')) return `${FILE_URL}${path}`;
  return `${FILE_URL}/${path}`;
};

const AdminCustomer = () => {
  const { getItem } = useTokens();
  const adminAuthToken = getItem('admin', 'AuthToken');
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { fetchPages, fetchPage, createPage, updatePage, deletePage } = usePageAPI();
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', password: '', roles: 'ROLE_CUSTOMER', status: 1 });
  const [pageData, setPageData] = useState([]);
  const [brandData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchBy, setSearchBy] = useState('name');
  const [searchValue, setSearchValue] = useState('');
  const [isReload, setIsReload] = useState(false);

  const pageType = {
    isNew: pathname.includes('new'),
    isEdit: pathname.includes('edit'),
    isList: !(pathname.includes('new') || pathname.includes('edit')),
  };

  const fetchData = async () => {
    const params = new URLSearchParams(search);
    const page = Number(params.get('page')) || 1;
    const limit = Number(params.get('limit')) || 20;
    const searchBy = params.get('searchBy') || 'title';
    const searchValue = params.get('searchValue') || '';

    // Setting state values based on URL parameters
    setCurrentPage(page);
    setLimit(limit);
    setSearchBy(searchBy);
    setSearchValue(searchValue);

    if (pageType.isList) {
      const toastId = toast.loading('Loading users...', { autoClose: false });

      try {
        // Wait for the axios request to finish before updating the state
        const response = await axios.get(`${API_URL}/admin/users`, {
          params: {
            limit,
            page,
            searchBy,
            searchValue,
            roles: 'ROLE_CUSTOMER'
          },
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminAuthToken}`,
          },
        });

        // Set data after response
        setPageData({
          data: response.data.data,
          dataCount: response.data.dataCount,
        });

        // Success toast update
        toast.update(toastId, {
          render: 'Users loaded successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to load users. Please try again later.';

        // Error toast update
        toast.update(toastId, {
          render: errorMessage,
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [pathname, search]);
  
  useEffect(() => {
    if (isReload) {
      fetchData();        // Re-fetch on reload
      setIsReload(false); // Reset the reload flag
    }
  }, [isReload]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const toastId = toast.loading('Saving your settings...', { autoClose: false });

    // Validate required fields
    const { name, password, phone, email } = formData;

    try {
      const dataToSend = {
        status: formData.status,
        roles: 'ROLE_CUSTOMER',
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone,
        email,
        password: xorEncrypt(password),
      };

      const response = await axios.post(`${API_URL}/admin/user`, dataToSend, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminAuthToken}`,
        },
      });

      if (response.status === 201) {
        toast.update(toastId, {
          render: 'User Created successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
        navigate(`/admin/customer/edit/${response.data.user.id}`);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to create user.';
      toast.update(toastId, {
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this page?")) {
      const toastId = toast.loading('Deleting ...', { autoClose: false });
      const success = await deletePage({ type: 'admin/user', id: id, setLoading: setLoading });
      if (success) {
        toast.update(toastId, {
          render: success || `Page deleted successfully!`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
        fetchData();
      } else {
        toast.update(toastId, {
          render: 'Failed to delete page.',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };

  const handleStatusToggle = async (id, status) => {
    const toastId = toast.loading('Updating status...', { autoClose: false });

    try {
      await axios.put(`${API_URL}/admin/user/${id}`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${adminAuthToken}`,
          },
        }
      );

      toast.update(toastId, {
        render: 'Status updated successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Status update failed:', error);

      toast.update(toastId, {
        render: 'Failed to update status.',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const updateUrlParams = (page, limit, searchBy, searchValue) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', limit);
    params.set('searchBy', searchBy);
    params.set('searchValue', searchValue);
    navigate({ pathname: '/admin/customer/list', search: params.toString() });
  };

  return (
    <>
      <Head title={getPageTitle(pageType)} />

      {pageType.isNew ? (
        loading ? <LoadingDashboardContent progress={progress} /> :
          <FormLayout
            title={pageType.isEdit ? "Edit Customer" : "Add New Customer"}
            submitText={pageType.isEdit ? (loading ? 'Updating ...' : 'Update') : (loading ? 'Creating ...' : 'Create')}
            onCancel={() => navigate('/admin/customer')}
            onSubmit={handleFormSubmit}
            loading={loading}
          >
            <FormGenerator
              fields={[
                {
                  id: "roles", label: "Role", type: "select", options: {
                    'ROLE_CUSTOMER': 'Customer',
                  }, default: 'ROLE_CUSTOMER', required: true
                },
                { id: "first_name", label: "First Name", type: "text", class: 'col-md-6', required: true },
                { id: "last_name", label: "Last Name", type: "text", class: 'col-md-6', required: true },
                { id: 'phone', label: 'Phone Number', type: 'tel', class: 'col-md-6', required: true, prefix: '+91', minLength: 10, maxLength: 10, isNumeric: true },
                { id: 'email', label: 'Email', type: 'email', class: 'col-md-6', required: true },
                { id: "password", label: "User Password", type: "password", class: 'col-md-6', required: true },
              ]}
              fieldData={formData}
              onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
              authToken={adminAuthToken}
            />
            <FormGenerator
              fields={[
                { id: "status", label: "User Status", type: "select", options: { 1: "Active", 0: "Inactive" } },
              ]}
              fieldData={formData}
              onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
              authToken={adminAuthToken}
            />
          </FormLayout>
      ) : null}

      {pageType.isEdit ? (
        loading ? (
          <LoadingDashboardContent progress={progress} />
        ) : (
          <AdminCustomerEdit id={id} />
        )
      ) : null}

      {pageType.isList && (
        loading ? <LoadingDashboardContent progress={progress} /> :
          <PageList
            itemType={'users'}
            itemId={'basic.id'}
            pageTitle={'Customer List'}
            pageData={isReload ? [] : pageData.data}
            actionApplyKeyMap={{
              'Delete': 'delete',
              'Active': 'active',
              'Inactive': 'inactive',
            }}
            columns={['Customer', 'Phone', 'Email', 'State', 'Created at']}
            filterBy={{
              '-- select --': '',
              'Customer ID': 'id',
              'Customer Name': 'name',
              'Customer Phone': 'phone',
              'Customer Email': 'email',
              'Active Status': 'status',
              'DateRange': 'datetime',
            }}
            columnKeyMap={{
              'Customer': (item) => (
                <>
                  <LazyLoadImage imgSrc={getMediaUrl(item.basic?.profile_picture) || NO_IMG} alt="Image" className="list-image img-thumbnail lazy-image object-fit-cover" />
                  <Link className='a_edit ms-2' to={`/admin/customer/edit/${item.basic?.id}`}>{item.basic?.first_name} {item.basic?.last_name} {idFormat(item.basic?.id)}</Link>
                </>
              ),
              'Phone': (item) => item.basic?.phone,
              'Email': (item) => item.basic?.email,
              'State': (item) => (
                <Toggle
                  size="sm"
                  defaultChecked={item.basic?.status == true}
                  onChange={(checked) => handleStatusToggle(item.basic?.id, checked ? 1 : 0)}
                />
              ),
              'Created at': (item) => formatDateTime(item.basic?.createdOn),
            }}
            onAddNew={() => navigate('/admin/customer/new')}
            onView={(id) => navigate(`/admin/customer/view/${id}`)}
            onEdit={(id) => navigate(`/admin/customer/edit/${id}`)}
            onDelete={handlePageDelete}
            totalRows={pageData.dataCount}
            updateUrlParams={updateUrlParams}
            statusChange={{ 'name': 'status', 'route': 'user' }}
            isReload={setIsReload}
          />
      )}
    </>
  );
};

// Helper function for page title
const getPageTitle = ({ isEdit, isNew }) =>
  isEdit ? "Edit Customer" : isNew ? "Create New Customer" : "Customer List";

export default AdminCustomer;
